import React, { useMemo } from 'react'

import { useAbortController } from '@harvey-frontend/providers/abort-controller-provider'
import { useShallow } from 'zustand/react/shallow'

import { Button } from '@harvey-frontend/components/ui/button'

import { RequestMode } from '../../../utils/constants'
import { useRequestMode } from '../hooks/useRequestMode'
import { useAccessTokenContext } from '../providers/AccessTokenProvider'
import { useDialogContext } from '../providers/DialogProvider'
import useAppStore from '../stores/store'
import { getUploadedFiles } from '../utils/file-utils'
import { openFilePreviewer } from '../utils/office'
import Menu from './Menu'

const Step1Footer = () => {
  return (
    <div className="flex w-full justify-between">
      <Menu />
    </div>
  )
}

const Step2Footer = () => {
  const { getAbortController, removeAbortController } = useAbortController()
  const mode = useRequestMode()
  const {
    isFetching,
    suggestions,
    editSummary,
    files,
    queryCompleted,
    resetServerState,
  } = useAppStore(
    useShallow((state) => ({
      isFetching: state.isFetching,
      suggestions: state.suggestions,
      editSummary: state.editSummary,
      files: state.files,
      queryCompleted: state.queryCompleted,
      resetServerState: state.resetServerState,
    }))
  )

  const { dialogRef, closeDialog } = useDialogContext()
  const { getAccessToken } = useAccessTokenContext()
  const { textWithReferences, anyReferences } = useMemo(() => {
    if (mode === RequestMode.EDIT && editSummary) {
      return {
        textWithReferences: [
          {
            text: editSummary?.heading ?? '',
            references: editSummary?.references ?? [],
          },
        ],
        anyReferences: editSummary?.references?.length,
      }
    } else if (mode === RequestMode.REVISE && suggestions.length > 0) {
      return {
        textWithReferences: suggestions.map((suggestion) => ({
          text: suggestion.suggestion,
          references: suggestion?.references ?? [],
        })),
        anyReferences: suggestions.some(
          (suggestion) => suggestion?.references?.length
        ),
      }
    }
    return {
      textWithReferences: [],
      anyReferences: false,
    }
  }, [editSummary, suggestions, mode])

  const onCancel = () => {
    const abortController = getAbortController(mode)
    if (abortController) {
      abortController.abort()
      removeAbortController(mode)
    }
  }

  const onReset = () => {
    resetServerState()
  }

  const viewReferencesButton = anyReferences && (
    <Button
      data-testid="view-references-button"
      variant="secondary"
      size="sm"
      onClick={() =>
        openFilePreviewer(
          dialogRef,
          closeDialog,
          getAccessToken,
          textWithReferences,
          getUploadedFiles(files),
          // No reference selected.
          null
        )
      }
    >
      View references
    </Button>
  )

  return (
    <div className="flex w-full items-center justify-between">
      {isFetching && (
        <Button
          data-testid="cancel-button"
          size="sm"
          variant="destructive"
          onClick={onCancel}
        >
          Cancel
        </Button>
      )}
      {queryCompleted && (
        <>
          <div className="flex shrink-0 items-center gap-1">
            {viewReferencesButton}
          </div>
          <Button
            data-testid="clear-suggestions-button"
            size="sm"
            variant="destructive"
            onClick={onReset}
          >
            Clear {mode === RequestMode.EDIT ? 'edit summary' : 'suggestions'}
          </Button>
        </>
      )}
    </div>
  )
}

const Footer = () => {
  const { isFetching, suggestions } = useAppStore(
    useShallow((s) => ({
      isFetching: s.isFetching,
      suggestions: s.suggestions,
    }))
  )
  const shouldDisplayStep1Footer = suggestions.length === 0 && !isFetching

  return (
    <div className="h-8">
      {shouldDisplayStep1Footer && <Step1Footer />}
      {!shouldDisplayStep1Footer && <Step2Footer />}
    </div>
  )
}

export default Footer
